// İSG uzmanı portalı — İzin detayı: Aksiyonlar tabı = izin denetim izi (api-contract §35.1).
// İzne bağlı kronolojik zaman-çizelgesi: **SystemLog** (otomatik — durum geçişi [from→to],
// bilgi değişikliği [ChangeSet], departman/AI olayları, KKD-kontrol-ek belge ekle-sil) +
// **Comment** (kişi yazar). Yazma yetkisi backend'dedir (açan + reviewer); FE yorum kutusunu
// gösterir, yetkisiz 403'ü hata olarak yansıtır. Satır gövdesi (aktör/başlık/durum/departman/
// gerekçe/hedef/değişiklik listesi) `ActivityTimelineItem.tsx`'te PAYLAŞILIR — Kontrol aşamasının
// salt-okunur "AI notları + Aksiyonlar" kartı (`DepartmentReviewPanels.tsx`) AYNI DTO'yu ve AYNI
// bileşeni kullanır (§35.1 "§31.5 departman detayı aynı DTO'yu ve aynı listeyi taşır").
//
// **owner review B3-3 — `changeSet`/`authorUuid` şekil düzeltmesi (§35.1).** `authorUuid` yalan
// bir alandı (backend'de hiç yoktu), KALDIRILDI. `changeSet` her zaman dizi olarak bildirilmişti
// ama S35 ÖNCESİ backend'in kendisi onu ham JSON METİN döndürüyordu — bilgi değişikliği olan HER
// izinde bu sekme `TypeError` ile ÇÖKÜYORDU (dev DB'de doğrulandı, dosya başlığı `permits.ts`'te).
// S35 backend'i artık GERÇEKTEN projekte eder; canlı doğrulama: bir taslağı gönder/onayla → sekme
// artık BOŞ değil, satırlar durum geçişini Tag'lerle gösterir, konsolda hata YOK.
// Veri `GET /permits/{uuid}/activities` (api-contract §35.1, `permits/get` + görünürlük).
// Liste en yeni üstte gösterilir (API eskiden yeniye; `lib/activityOrder.ts`).

import {
  Alert,
  Button,
  EmptyState,
  Spinner,
  type WorkPermitActivityDto,
  permitsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newestFirst } from '../../lib/activityOrder';
import { permitsKeys } from '../../lib/permitsKeys';
import { TabPanelIntro } from '../TabPanelIntro';
import { ActivityAvatar, ActivityBody } from './ActivityTimelineItem';

interface PermitActivityPanelProps {
  permitUuid: string;
}

const commentInputClass =
  'w-full min-h-[84px] rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg ' +
  'placeholder:text-muted transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-60';

export function PermitActivityPanel({ permitUuid }: PermitActivityPanelProps) {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: permitsKeys.activities(permitUuid),
    queryFn: () => permitsApi.listPermitActivities(permitUuid),
    enabled: permitUuid.length > 0,
  });

  const activities = newestFirst(query.data ?? []);

  return (
    <div className="flex flex-col gap-3">
      <TabPanelIntro>{t('permits.activities.subtitle')}</TabPanelIntro>

      <CommentComposer permitUuid={permitUuid} />

      {query.isPending ? (
        <div className="flex min-h-[20vh] items-center justify-center">
          <Spinner label={t('permits.activities.loading')} />
        </div>
      ) : query.isError ? (
        <Alert tone="danger">{resolveApiErrorMessage(query.error, t)}</Alert>
      ) : activities.length === 0 ? (
        <EmptyState variant="panel" title={t('permits.activities.empty')} />
      ) : (
        // Zaman-çizelgesi bir `Table` DEĞİL (çok satırlı serbest metin + değişiklik seti taşır,
        // gerçek Carbon aktivite akışları da tablo değildir) — "Carbon list anatomy flush" ruhu
        // burada kutulu (`border` per-item) kartlar YERİNE Tile'a bitişik, saç çizgisi
        // (`divide-y`) ayrımlı düz bir liste olarak uygulanır (S33 Slice 6).
        <ol className="flex flex-col divide-y divide-border">
          {activities.map((activity) => (
            <ActivityRow key={activity.uuid} activity={activity} />
          ))}
        </ol>
      )}
    </div>
  );
}

/** Tek aksiyon kaydı — gövde `ActivityTimelineItem.tsx`'te PAYLAŞILIR (dosya başlığı notu). */
function ActivityRow({ activity }: { activity: WorkPermitActivityDto }) {
  return (
    <li className="flex gap-3 px-1 py-3 carbon:px-4">
      <ActivityAvatar kind={activity.kind} />
      <ActivityBody activity={activity} />
    </li>
  );
}

/** Yorum yazma kutusu — açan + reviewer yazabilir (backend kapılar; yetkisiz 403 hata). */
function CommentComposer({ permitUuid }: { permitUuid: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const trimmed = text.trim();

  const mutation = useMutation({
    mutationFn: () => permitsApi.addPermitActivity(permitUuid, { text: trimmed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.activities(permitUuid) });
      setText('');
    },
  });

  return (
    <form
      // Kutulu (`border`) şerit KALKAR — Tile'a bitişik bir üst şerit; dolu "Gönder" düğmesi de
      // ghost olur (S33 Slice 6, coordinator bulgusu: ekranın TEK dolu butonu Onayla kalmalı).
      className="flex flex-col gap-2 border-b border-border px-1 pb-3 carbon:px-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed.length > 0) {
          mutation.mutate();
        }
      }}
    >
      <label htmlFor="permit-activity-comment" className="text-sm font-medium text-fg">
        {t('permits.activities.commentLabel')}
      </label>
      <textarea
        id="permit-activity-comment"
        className={commentInputClass}
        placeholder={t('permits.activities.commentPlaceholder')}
        value={text}
        disabled={mutation.isPending}
        onChange={(e) => setText(e.target.value)}
      />
      {mutation.isError ? (
        <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
      ) : null}
      <div className="flex justify-end">
        <Button
          type="submit"
          variant="ghost"
          size="sm"
          loading={mutation.isPending}
          disabled={trimmed.length === 0 || mutation.isPending}
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {t('permits.activities.commentSubmit')}
        </Button>
      </div>
    </form>
  );
}
